import { initializeApp, getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, initializeFirestore, type Firestore } from 'firebase/firestore';

const firebaseConfig = {
  apiKey:            process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain:        process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

const isBrowser = typeof window !== 'undefined';

const app = isBrowser
  ? (getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0])
  : null;

// App Check — chống lạm dụng Firestore/Storage từ client GIẢ (script bên ngoài app thật).
// CHỈ init khi có site key reCAPTCHA v3 (NEXT_PUBLIC_FIREBASE_APPCHECK_KEY) → no-op tới khi cấu hình
// ⇒ deploy AN TOÀN, không gãy gì. Bật ENFORCE ở Firebase Console (rollout riêng, xem docs).
// ⚠️ Project dùng CHUNG GMS+GBC → enforce đòi CẢ 2 app gửi token; chạy chế độ monitoring trước.
//
// ⚡ DYNAMIC IMPORT (2026-09-05, tối ưu tốc độ tải mobile): `firebase/app-check` + reCAPTCHA v3 nặng
//    ~30 KB gz và TRƯỚC ĐÂY nằm trong chunk khởi động dù key CHƯA hề được cấu hình → mọi user tải
//    thừa mỗi lần vào app. Nay chỉ tải khi thực sự có key.
//    ⚠️ KHI BẬT ENFORCE: init lùi lại vài trăm ms so với trước → hãy kiểm tra request Firestore đầu
//    tiên có kèm App Check token không (Console → App Check → Metrics) trước khi chuyển sang enforce.
if (app && isBrowser) {
  const appCheckKey = process.env.NEXT_PUBLIC_FIREBASE_APPCHECK_KEY;
  if (appCheckKey) {
    void import('firebase/app-check')
      .then(({ initializeAppCheck, ReCaptchaV3Provider }) => {
        try {
          initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(appCheckKey),
            isTokenAutoRefreshEnabled: true,
          });
        } catch { /* đã init / lỗi cấu hình — KHÔNG chặn app */ }
      })
      .catch(() => { /* không tải được module — KHÔNG chặn app */ });
  }
}

export const getFirebaseAuth = () => getAuth(app!);

// ── CACHE BỀN CỦA FIRESTORE (IndexedDB) — bật 23/09, TẮT 24/09, BẬT LẠI 24/09, TẮT HẲN 26/09 ────
// 🔴 LỊCH SỬ SỰ CỐ (xem memory project_firestore_persistent_cache_incident): bật
// `persistentLocalCache`+`persistentMultipleTabManager` gây crash-loop production thật LẦN 1 (24/09,
// SDK 12.13.0, NV Thuý /settings — "INTERNAL ASSERTION FAILED... Attempt to get a record from database
// without an in-progress transaction", 6.974 dòng/4 phút). Đã nâng SDK lên 12.18.0 rồi bật lại + thêm
// lưới an toàn (AppShell.tsx bắt lỗi này ở window.onerror, tự reload 1 lần) vì bản 12.14.0 có mục
// changelog "Improved robustness... query listen stream" tưởng liên quan.
// LẦN 2 (26/09, cùng SDK 12.18.0, CHÍNH lưới an toàn vừa thêm): CÙNG assertion ID b815 tái phát, lần
// này với context khác — "Attempt to iterate a cursor that doesn't exist" — và nổ ra qua UNHANDLED
// PROMISE REJECTION (không phải window.onerror đồng bộ) nên lưới an toàn (chỉ canh window.onerror)
// KHÔNG bắt được: 1 tab của NV Thuý chạy suốt 8,5 TIẾNG với lỗi này lặp lại ~190 lần, /timeline không
// còn nhận cập nhật Firestore real-time nào mà không ai biết (silent stale data — còn nguy hiểm hơn
// crash-loop lần 1 vì lần 1 ít nhất user THẤY trang lỗi). Kết luận: SDK 12.18.0 CHƯA thực sự vá dứt
// điểm b815, chỉ đổi hình dạng lỗi. Đã TẮT HẲN, lùi về `getFirestore()` mặc định — hành vi ổn định
// trước 23/09. ĐỪNG bật lại nữa cho tới khi có xác nhận CHÍNH THỨC từ Google (không phải suy luận từ
// changelog) rằng b815 đã hết.

// ── Bug WebKit/Safari: listener Firestore (onSnapshot) CHẬM 30s (vá 15/10/2026) ─────────────────────
// Đo thật: NV mở `/conversations` từ icon Dock Safari macOS, listener `messages` mất **31.153ms**
// mới có callback đầu — trùng khớp gần như tuyệt đối (mili-giây) với báo cáo cộng đồng Safari 26.4+:
// kỹ sư Firebase (dlarocque) xác nhận TRỰC TIẾP đây là bug WebKit — "receives payload ~100ms nhưng
// giữ trong buffer nội bộ tới khi có gói keep-alive 30s từ server mới xả ra" — KHÔNG phải lỗi app/cache
// nào ở GMS (xem github.com/firebase/firebase-js-sdk/issues/9789, cũng KHÔNG liên quan tới sự cố
// persistentLocalCache ở trên — GMS chưa hề bật persistence, bug này xảy ra cả với `getFirestore()` trần).
// Chỉ xảy ra trên WebKit (Safari macOS/iOS) — Chrome/Firefox không dính (xác nhận bởi nhiều báo cáo
// độc lập). Fix CHỈ áp cho WebKit để không đổi hành vi (long-polling kém hiệu quả hơn streaming) cho
// đa số user dùng Chrome: `experimentalForceLongPolling` ép dùng kênh XHR long-polling cũ thay vì
// đường Fetch Streams bị WebKit giữ buffer — cộng đồng đo A/B xác nhận hết hẳn (29.980ms → ~200ms,
// cùng issue trên). Bug đã "RESOLVED FIXED" trong mã nguồn WebKit (bugs.webkit.org #322545, 27/08/2026)
// nhưng CHƯA rõ đã lên bản Safari phát hành nào — giữ fix này cho tới khi xác nhận Safari đã vá.
// `initializeFirestore` chỉ gọi được ĐÚNG 1 LẦN/app → cache instance, KHÔNG gọi `getFirestore()` ở
// nơi khác cho app này (đã audit: `lib/firebase.ts` là nơi DUY NHẤT gọi getFirestore(app) ở client).
function isSafariOrWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isMacSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIOS || isMacSafari;
}

let firestoreInstance: Firestore | null = null;
export const getFirebaseDb = (): Firestore => {
  firestoreInstance ??= isSafariOrWebKit()
    ? initializeFirestore(app!, { experimentalForceLongPolling: true })
    : getFirestore(app!);
  return firestoreInstance;
};

// ⚠️ KHÔNG export getFirebaseStorage nữa: không nơi nào trong app dùng (mọi upload đi qua API route
// với Admin SDK), mà `import { getStorage } from 'firebase/storage'` kéo cả SDK Storage vào chunk
// khởi động. Cần upload TỪ CLIENT thì dynamic-import `firebase/storage` ngay tại chỗ dùng.
